import { useRolePermissions, useRoles } from "@api/roles";
import i18n from "@i18n/i18n";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";

const RoleMappingGrid = observer(() => {
  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [availablePermissions, setAvailablePermissions] = useState<string[]>([]);
  const [defaultPermissions, setDefaultPermissions] = useState<string[]>([]);

  const getRoles = useRoles();
  const getRolePermissions = useRolePermissions(selectedRoleId);

  // Update permissions when role permissions are loaded (view-only)
  useEffect(() => {
    if (getRolePermissions.data) {
      setAvailablePermissions(getRolePermissions.data.availablePermissions || []);
      setSelectedPermissions(getRolePermissions.data.selectedPermissions || []);
      setDefaultPermissions(getRolePermissions.data.defaultPermissions || []);
    }
  }, [getRolePermissions.data]);

  const handleRoleChange = (roleId: string) => {
    setSelectedRoleId(roleId);
    setSelectedPermissions([]);
    setAvailablePermissions([]);
    setDefaultPermissions([]);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        gap: 2,
      }}
    >
      {/* Role Selection */}
      <Card>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel id="role-select-label">{i18n.t("role-mapping-tab.select-role.label")}</InputLabel>
            <Select
              labelId="role-select-label"
              value={selectedRoleId}
              label={i18n.t("role-mapping-tab.select-role.label")}
              onChange={(e) => handleRoleChange(e.target.value)}
              disabled={getRoles.isFetching}
            >
              {getRoles.data?.roles?.map((role) => (
                <MenuItem key={role.roleId} value={role.roleId || ""}>
                  {role.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {/* Permissions (read-only) */}
      {selectedRoleId && (
        <Card sx={{ flexGrow: 1, overflow: "auto" }}>
          <CardContent>
            <Stack spacing={3}>
              <Typography variant="h6">{i18n.t("role-mapping-tab.permissions.title")}</Typography>

              {getRolePermissions.isFetching ? (
                <Typography>{i18n.t("role-mapping-tab.permissions.loading")}</Typography>
              ) : availablePermissions.length === 0 ? (
                <Typography color="text.secondary">
                  No permissions available for this role. Check console for API response.
                </Typography>
              ) : (
                <FormGroup>
                  {availablePermissions.map((permissionName) => {
                    const isDefault = defaultPermissions.includes(permissionName);

                    return (
                      <FormControlLabel
                        key={permissionName}
                        control={
                          <Checkbox checked={selectedPermissions.includes(permissionName)} disabled />
                        }
                        label={
                          <Box>
                            <Typography variant="body1" color="text.secondary">
                              {permissionName}
                              {isDefault && (
                                <Typography component="span" variant="caption" sx={{ ml: 1, fontStyle: "italic" }}>
                                  (Default)
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        }
                      />
                    );
                  })}
                </FormGroup>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
});

export default RoleMappingGrid;
